import { createFileRoute } from "@tanstack/react-router";

import {
  ServiceIntentPage,
  type ServiceFaq,
  type ServiceSection,
} from "@/components/seo/ServiceIntentPage";
import {
  AD_SPEND_NOTE,
  AGENCY_OFFER,
  AUDIT_OFFER,
  DEVELOPER_OFFER,
  LAUNCH_OFFER,
  FIT_REVIEW_NOTE,
} from "@/lib/pricing";
import { servicePageHead } from "@/lib/service-page-seo";

const PATH = "/real-estate-lead-generation-meta-ads";
const TITLE = "Meta Ads for Real Estate Agencies & Developers | Adkiro";
const DESCRIPTION = `Facebook and Instagram advertising for property agencies, brokers and developers in Dubai, Europe and the US: property launches ${LAUNCH_OFFER.price} and monthly management ${AGENCY_OFFER.price.toLowerCase()}.`;

const SECTIONS: ServiceSection[] = [
  {
    eyebrow: "Listings and projects",
    title: "Match each campaign to the property and its buyer",
    copy: "A studio in Dubai Marina, a family home in Texas and an off-plan project in Sofia sell to different people. Campaigns are built around the property, its price, its location and the buyer it has to reach.",
    points: [
      "Campaigns structured around listings, projects and locations",
      "The price and key facts in the ad, so enquiries arrive already informed",
      "Meta's housing rules applied from the start, not after a rejection",
      "Creative for buyers, investors or sellers, according to the goal",
    ],
  },
  {
    eyebrow: "Enquiries",
    title: "Built for enquiries your team will call back",
    copy: "Instant form, WhatsApp or landing page, chosen for the listing and the buyer. The form asks what your sales team needs to know, and the campaign is optimised for enquiries that turn into conversations.",
    points: [
      "Instant forms or WhatsApp, chosen per campaign",
      "Form questions agreed with your sales team",
      "Enquiry quality reviewed with you, not only the number",
      "Lead data stays in your own systems",
    ],
  },
  {
    eyebrow: "Creative and reporting",
    title: "From listing photos to a repeatable testing programme",
    copy: "Listing photos, floor plans, video tours and location benefits become creatives tested against each other. Reports are written, and keep what the ads did separate from what sales did.",
    points: [
      "Carousels, square and vertical versions made from your own photos",
      "Tests across price points, locations, formats and messages",
      "Creative refreshed before local audiences tire of it",
      "Written reports, weekly or monthly depending on the service",
    ],
  },
];

const STEPS = [
  {
    title: "Free review",
    copy: "Send your website, the listings or project and your budget. Kristian replies in writing within one business day.",
  },
  {
    title: "A proposal with a price",
    copy: "Which service fits, what it costs and what happens first, agreed in writing.",
  },
  {
    title: "Pay online and launch",
    copy: "Card, Apple Pay or Google Pay through Stripe. Work starts when the photos or the access arrive.",
  },
];

const FAQS: ServiceFaq[] = [
  {
    q: "Does Adkiro have property advertising experience?",
    a: "Yes. Kristian advertises listings for his own property business in Sofia on Meta, and applies the same methods to agencies, brokers and developers. Each campaign is planned around its own market, price and buyer.",
  },
  {
    q: "What does it cost?",
    a: `${LAUNCH_OFFER.name}: ${LAUNCH_OFFER.price} for one listing or project, including 30 days of management. ${AUDIT_OFFER.name}: ${AUDIT_OFFER.price}. Monthly management: ${AGENCY_OFFER.price.toLowerCase()} a month for agencies and brokers, ${DEVELOPER_OFFER.price.toLowerCase()} for developers, and by proposal for several projects or markets. ${AD_SPEND_NOTE}`,
  },
  {
    q: "How do you judge a property campaign?",
    a: "By the enquiries your team would call back, the viewings they lead to and, where you share them, the sales. No responsible partner can guarantee a cost per lead or a sale.",
  },
  {
    q: "Do you call or qualify the leads?",
    a: "No. Adkiro designs the form questions and reviews enquiry quality with you; your team or sales partner owns the contact and the follow-up.",
  },
  {
    q: "Can campaigns reach international buyers?",
    a: "Yes, where you can legally market the property in each country. Campaigns for international buyers are planned by market and language, and local-language copy is reviewed or supplied by you before launch.",
  },
];

export const Route = createFileRoute("/real-estate-lead-generation-meta-ads")({
  head: () =>
    servicePageHead({
      path: PATH,
      title: TITLE,
      description: DESCRIPTION,
      serviceName: "Meta advertising for property",
      serviceType: "Facebook and Instagram advertising for real estate",
      audience: ["Real estate agencies", "Brokers", "Property developers"],
      faqs: FAQS,
    }),
  component: RealEstateMetaAdsPage,
});

function RealEstateMetaAdsPage() {
  return (
    <ServiceIntentPage
      badge="Meta ads for property · Dubai · Europe · US"
      title="Meta advertising for property agencies, brokers and developers"
      intro="Founder-led Facebook and Instagram campaigns, made from your own listing photos and built to bring serious buyers, not form-fillers."
      qualification="Every market is different. Each campaign is planned around the property, its location and price, Meta's housing rules and how your team follows up enquiries."
      metrics={[
        { value: AUDIT_OFFER.price, label: "Account review" },
        { value: LAUNCH_OFFER.price, label: "Property launch" },
        { value: AGENCY_OFFER.price.replace("From ", "") + "+", label: "Agencies, per month" },
        { value: DEVELOPER_OFFER.price.replace("From ", "") + "+", label: "Developers, per month" },
      ]}
      metricsContext={AD_SPEND_NOTE}
      sections={SECTIONS}
      steps={STEPS}
      faqs={FAQS}
      ctaTitle="Start with a free review"
      ctaCopy={FIT_REVIEW_NOTE}
    />
  );
}
